import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../../api';
import './AdminUserDetailPage.css';

const AdminUserDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({ contactName: '', phoneNumber: '', telegramNick: '', accountType: 'individual' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        adminService.getUser(id)
            .then(({ data }) => {
                setUser(data);
                setForm({
                    contactName: data.contactName || '',
                    phoneNumber: data.phoneNumber || '',
                    telegramNick: data.telegramNick || '',
                    accountType: data.accountType || 'individual',
                });
            })
            .catch(() => setError('Не удалось загрузить пользователя.'))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminService.updateUser(id, form);
            load();
        } catch {
            alert('Не удалось сохранить изменения.');
        } finally {
            setSaving(false);
        }
    };

    const toggleBan = async () => {
        setBusy(true);
        try {
            if (user.isBanned) await adminService.unbanUser(id);
            else await adminService.banUser(id);
            load();
        } catch (e) {
            alert(e.response?.data ?? 'Не удалось изменить статус блокировки.');
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="admin-user-detail"><div className="admin-user-detail__placeholder">Загрузка...</div></div>;
    if (error || !user) return <div className="admin-user-detail"><div className="admin-user-detail__placeholder">{error}</div></div>;

    return (
        <div className="admin-user-detail">
            <button className="admin-user-detail__back" onClick={() => navigate('/admin/users')}>← К списку пользователей</button>

            <div className="admin-user-detail__header">
                <h1 className="admin-user-detail__title">{user.contactName || user.userName}</h1>
                {user.isBanned
                    ? <span className="admin-user-detail__badge admin-user-detail__badge--banned">Заблокирован</span>
                    : <span className="admin-user-detail__badge admin-user-detail__badge--active">Активен</span>}
            </div>

            <div className="admin-user-detail__stats">
                <div><b>{user.adCount}</b> объявлений</div>
                <div><b>{user.commentCount}</b> комментариев</div>
                <div>Роли: {user.roles.length > 0 ? user.roles.join(', ') : '—'}</div>
                <div>Регистрация: {new Date(user.registeredAt).toLocaleDateString('ru-RU')}</div>
                <div>Email: {user.email} {user.emailConfirmed ? '(подтверждён)' : '(не подтверждён)'}</div>
            </div>

            <button
                className={`admin-user-detail__btn ${user.isBanned ? '' : 'admin-user-detail__btn--danger'}`}
                disabled={busy}
                onClick={toggleBan}
            >
                {user.isBanned ? 'Разблокировать пользователя' : 'Заблокировать пользователя'}
            </button>

            <form className="admin-user-detail__form" onSubmit={save}>
                <h2 className="admin-user-detail__form-title">Данные профиля</h2>

                <div className="admin-user-detail__field">
                    <label>Имя для связи</label>
                    <input value={form.contactName} onChange={e => handleChange('contactName', e.target.value)} />
                </div>

                <div className="admin-user-detail__field">
                    <label>Телефон</label>
                    <input value={form.phoneNumber} onChange={e => handleChange('phoneNumber', e.target.value)} />
                </div>

                <div className="admin-user-detail__field">
                    <label>Telegram</label>
                    <input value={form.telegramNick} onChange={e => handleChange('telegramNick', e.target.value)} />
                </div>

                <div className="admin-user-detail__field">
                    <label>Тип аккаунта</label>
                    <select value={form.accountType} onChange={e => handleChange('accountType', e.target.value)}>
                        <option value="individual">Частное лицо</option>
                        <option value="company">Компания</option>
                    </select>
                </div>

                <button type="submit" className="admin-user-detail__save-btn" disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
            </form>

            <Link to={`/admin/ads?userId=${user.id}`} className="admin-user-detail__link">
                Смотреть объявления пользователя →
            </Link>
        </div>
    );
};

export default AdminUserDetailPage;
